import { X, Plus, Minus } from 'lucide-react';
import { CartItem as CartItemType } from '@/types';
import { formatPrice, calculateDiscount, calculateVariationDiscount, hasDiscount } from '@/lib/utils';
import { Link } from 'react-router-dom';
import { useStoreInfo } from '@/lib/store';

interface CartItemProps {
  item: CartItemType;
  onQuantityChange: (id: number, quantity: number) => void;
  onVariationQuantityChange: (itemId: number, variation: string, change: number) => void;
  onRemoveVariation: (itemId: number, variation: string) => void;
  onRemoveFromCart: (id: number) => void;
  soldOutItems: number[];
  stockLevels: Record<number, { total: number; variations: Record<string, number> }>;
}

export default function CartItem({
  item,
  onQuantityChange,
  onVariationQuantityChange,
  onRemoveVariation,
  onRemoveFromCart,
  soldOutItems,
  stockLevels,
}: CartItemProps) {
  const { storeInfo } = useStoreInfo();
  const isSoldOut = soldOutItems.includes(item.id);
  const stockLevel = stockLevels[item.id];

  // Calculate total quantity for discount
  const totalQuantity = item.selectedVariations 
    ? item.selectedVariations.reduce((sum, v) => sum + v.quantity, 0)
    : item.quantity;

  const discountInfo = calculateDiscount(item, totalQuantity);

  const getVariationAmount = (variation: string) => {
    const variationData = item.variations.find(v => v.variation === variation);
    return variationData ? (parseFloat(variationData.variation_amount) || 0) : 0;
  };

  return (
    <li className={`p-6 ${isSoldOut ? 'opacity-50' : ''}`}>
      <div className="flex items-start gap-4">
        <Link to={`/product/${item.id}`} className="relative flex-shrink-0">
          <img
            src={item.images[0]}
            alt={item.produt_name}
            className="w-24 h-24 object-cover rounded-lg"
          />
          {isSoldOut && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-lg">
              <span className="text-white font-bold text-sm">SOLD OUT</span>
            </div>
          )}
        </Link>

        <div className="flex-1 min-w-0">
          <div className="flex justify-between">
            <Link to={`/product/${item.id}`}>
              <h3 className="text-base font-medium text-gray-900 dark:text-white truncate hover:text-brand-primary dark:hover:text-brand-light transition-colors">
                {item.produt_name}
              </h3>
            </Link>
            <button
              onClick={() => onRemoveFromCart(item.id)}
              className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {item.selectedVariations ? (
            <div className="mt-2 space-y-2">
              {item.selectedVariations.map((variation) => (
                <div key={variation.variation} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        {variation.variation}
                      </span>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        ({formatPrice(
                          (item.variations.find(v => v.variation === variation.variation)
                            ? parseFloat(item.variations.find(v => v.variation === variation.variation)!.variation_amount)
                            : (parseFloat(String(variation.variation_amount).replace(/[^0-9.-]+/g, '')) || 0)
                          ) || 0,
                          storeInfo?.currenCy
                        )})
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => onVariationQuantityChange(item.id, variation.variation, -1)}
                        disabled={isSoldOut}
                        className="p-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                      >
                        <Minus className="h-4 w-4" />
                      </button>
                      <span className="w-8 text-center text-gray-900 dark:text-white">
                        {variation.quantity}
                      </span>
                      <button
                        onClick={() => onVariationQuantityChange(item.id, variation.variation, 1)}
                        disabled={isSoldOut || (stockLevel?.variations[variation.variation] || 0) <= variation.quantity}
                        className="p-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                      >
                        <Plus className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => onRemoveVariation(item.id, variation.variation)}
                        className="ml-2 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Individual variation pricing and discount */}
                  <div className="flex justify-between items-center text-sm">
                    {(() => {
                      // Determine unit amount: prefer canonical variation amount from item.variations, otherwise use selected variation amount, finally fall back to product selling price
                      const canonicalVar = item.variations.find(v => v.variation === variation.variation);
                      const rawVarAmount = canonicalVar
                        ? (parseFloat(canonicalVar.variation_amount) || 0)
                        : (parseFloat(String(variation.variation_amount).replace(/[^0-9.-]+/g, '')) || 0);
                      const selling = parseFloat(String(item.sellingprice).replace(/[^0-9.-]+/g, '')) || 0;
                      const unitAmount = rawVarAmount > 0 ? rawVarAmount : selling;
                      const variationData = canonicalVar;
                      const variationForDiscount = variationData ? variationData : { ...variation, variation_amount: String(unitAmount) } as any;
                      const discount = calculateVariationDiscount(item, variationForDiscount, variation.quantity);
                      const baseTotal = unitAmount * variation.quantity;
                      const finalTotal = discount?.isEligible ? baseTotal - discount.discountValue : baseTotal;

                      return (
                        <>
                          <span className="text-gray-600 dark:text-gray-400">
                            {variation.quantity} × {formatPrice(unitAmount, storeInfo?.currenCy)}
                          </span>
                          <div className="text-right">
                            {discount?.isEligible && discount.discountValue > 0 ? (
                              <div>
                                <span className="text-gray-500 dark:text-gray-400 line-through text-xs">
                                  {formatPrice(baseTotal, storeInfo?.currenCy)}
                                </span>
                                <span className="font-medium text-gray-900 dark:text-white ml-2">
                                  {formatPrice(finalTotal, storeInfo?.currenCy)}
                                </span>
                              </div>
                            ) : (
                              <span className="font-medium text-gray-900 dark:text-white">
                                {formatPrice(baseTotal, storeInfo?.currenCy)}
                              </span>
                            )}
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              ))}
              
              {/* Discount Information for Variations */}
              {hasDiscount(item) && (
                <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <div className="text-sm">
                    <p className="text-gray-700 dark:text-gray-300">
                      <span className="font-medium">Discount:</span> Buy {item.discount_qty}+ items for{' '}
                      {item.discount_type === 'percentage' 
                        ? `${item.discount_off}% OFF`
                        : `${formatPrice(parseFloat(String(item.discount_off).replace(/[^0-9.-]+/g, '')) || 0, storeInfo?.currenCy)} OFF`} each
                    </p>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">
                      Current quantity: {totalQuantity} 
                      {discountInfo.isEligible ? (
                        <span className="text-green-600 dark:text-green-400 font-medium ml-2">
                          ✓ Discount Applied! Save {formatPrice(discountInfo.discountValue, storeInfo?.currenCy)}
                        </span>
                      ) : (
                        <span className="text-orange-600 dark:text-orange-400 ml-2">
                          (Need {parseInt(item.discount_qty) - totalQuantity} more for discount)
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="mt-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Price: {formatPrice(parseFloat(String(item.sellingprice).replace(/[^0-9.-]+/g, '')) || 0, storeInfo?.currenCy)}
                </span>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => onQuantityChange(item.id, item.quantity - 1)}
                    disabled={isSoldOut || item.quantity <= 1}
                    className="p-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    <Minus className="h-4 w-4" />
                  </button>
                  <span className="w-8 text-center text-gray-900 dark:text-white">
                    {item.quantity}
                  </span>
                  <button
                    onClick={() => onQuantityChange(item.id, item.quantity + 1)}
                    disabled={isSoldOut || (stockLevel?.total || 0) <= item.quantity}
                    className="p-1 rounded-md bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {/* Discount Information for Regular Items */}
              {hasDiscount(item) && (
                <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                  <div className="text-sm">
                    <p className="text-gray-700 dark:text-gray-300">
                      <span className="font-medium">Discount:</span> Buy {item.discount_qty}+ items for{' '}
                        {item.discount_type === 'percentage' 
                        ? `${item.discount_off}% OFF`
                        : `${formatPrice(parseFloat(String(item.discount_off).replace(/[^0-9.-]+/g, '')) || 0, storeInfo?.currenCy)} OFF`} each
                    </p>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">
                      Current quantity: {totalQuantity} 
                      {discountInfo.isEligible ? (
                        <span className="text-green-600 dark:text-green-400 font-medium ml-2">
                          ✓ Discount Applied! Save {formatPrice(discountInfo.discountValue, storeInfo?.currenCy)}
                        </span>
                      ) : (
                        <span className="text-orange-600 dark:text-orange-400 ml-2">
                          (Need {parseInt(item.discount_qty) - totalQuantity} more for discount)
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </li>
  );
}